import classnames from "classnames";
import React, { Fragment, useCallback, useMemo } from "react";

import { determineDeliveryTimeTranslationKey } from "@utils/deliveryPromise";
import { DeliveryTypes } from "@utils/enums";
import { splitPrice } from "@utils/productPrice";

import { TPreferredStoreState } from "@redux/modules/api/data/preferredStore/preferredStore.types";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { Input } from "@components/General/Form/input.component";
import { Tooltip } from "@components/General/Tooltip/tooltip.component";
import { AdditionalService } from "@components/Product/ECommerce/delivery/deliveryBlock/deliveryItem/additionalService/additionalService.container";
import BoxPromise from "@components/Product/ECommerce/delivery/deliveryBlock/deliveryItem/boxPromise/boxPromise.component";
import ItemStock from "@components/Product/ECommerce/delivery/deliveryBlock/deliveryItem/itemStock/itemstock.component";
import {
    DeliveryOption,
    ECommerceProduct,
    Tooltips,
} from "@components/Product/ECommerce/eCommerce.types";
import { StorePickerModal } from "@components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@components/Store/StorePicker/types/storePickerType.enum";

import { AdditionalServiceModel } from "lib/@types/service/shared";

import "./deliveryItem.scss";

export const formatPriceForEcommerce = (price: number) => {
    const splitted = splitPrice(price);
    return `${splitted[0]},${splitted[1] === "00" ? "-" : splitted[1]}`;
};

type Props = {
    preferredStore?: TPreferredStoreState | null;
    option: DeliveryOption;
    checked: boolean;
    product?: ECommerceProduct;
    handleClick: (deliveryType: DeliveryTypes) => void;
    minimumOrderQuantity: number;
    roundingValue: number;
    xfPath?: string | null;
    isLimitedStock: boolean;
    tooltips: Tooltips;
    infoLink: string;
    deliveryTypesDisabled: boolean;
    onServiceProductClick: (serviceProduct: AdditionalServiceModel) => void;
    activeServiceProducts: string[];
};

const DeliveryItem = ({
    option,
    checked,
    handleClick,
    preferredStore,
    minimumOrderQuantity,
    roundingValue,
    xfPath,
    isLimitedStock,
    tooltips,
    infoLink,
    product,
    deliveryTypesDisabled,
    onServiceProductClick,
    activeServiceProducts,
}: Props) => {
    const [isStorePickerOpen, setIsStorePickerOpen] = React.useState(false);
    const { t } = useGranite();
    const deliveryTimeUnitTk = t(
        `product.deliveryPromise.timeUnits.${option.deliveryTimeUnit}`,
    );

    const productAvailability = preferredStore?.productAvailability;

    const deliveryTooltipItem = tooltips[option.deliveryType];

    const productIsClickAndCollect = useMemo(
        () => !!product?.deliveryTypes.includes(DeliveryTypes.ClickAndCollect),
        [product?.deliveryTypes],
    );

    const handleOnClose = useCallback(
        () => setIsStorePickerOpen(false),
        [setIsStorePickerOpen],
    );

    return (
        <>
            <div>
                <div className="delivery-item__wrapper">
                    <div>
                        <Input
                            data-test={`deliveryItem-${option.deliveryType}-input`}
                            type="radio"
                            id={option.deliveryType}
                            name={option.deliveryType}
                            value={option.deliveryType}
                            onClick={() => handleClick(option.deliveryType)}
                            checked={checked && !deliveryTypesDisabled}
                            disabled={deliveryTypesDisabled}
                        />
                        <label
                            data-test={`deliveryItem-${option.deliveryType}-label`}
                            className={"m-0"}
                            htmlFor={option.deliveryType}
                        >
                            {t(
                                `general.ecommerce.radio.deliveryType.${option.deliveryType}`,
                            )}
                            {deliveryTooltipItem && (
                                <Tooltip
                                    data-test={`deliveryItem-${option.deliveryType}-tooltip`}
                                    title={deliveryTooltipItem.title}
                                    innerHTML={deliveryTooltipItem.text}
                                />
                            )}
                        </label>
                    </div>
                    {(product?.montageParents || []).length === 0 && (
                        <div
                            data-test={`deliveryItem-${option.deliveryType}-deliveryCost`}
                            className="d-flex justify-content-end"
                        >
                            <p
                                className={classnames(
                                    "font-weight-bold m-0 text-right delivery-item__cost",
                                )}
                            >
                                {option.deliveryCost === 0 ? (
                                    t(
                                        `general.ecommerce.deliveryCost.free.${option.deliveryType}`,
                                    )
                                ) : (
                                    <Fragment>
                                        {formatPriceForEcommerce(
                                            option.deliveryCost,
                                        )}
                                    </Fragment>
                                )}
                            </p>
                        </div>
                    )}
                </div>
                {option.deliveryType === DeliveryTypes.ClickAndCollect ? (
                    <div>
                        {isLimitedStock && (
                            <p
                                data-test={`deliveryItem-${option.deliveryType}-description`}
                                className="delivery-item__description"
                            >
                                {t(
                                    "general.ecommerce.clickAndCollect.limitedStock",
                                )}
                            </p>
                        )}
                        {product?.boxArticle && (
                            <BoxPromise
                                data-test={`deliveryItem-${option.deliveryType}-boxPromise`}
                                boxLogisticInformation={
                                    product.boxLogisticInformation ||
                                    t(
                                        `ecommerce.label.pickup.box.${product.sku}`,
                                    )
                                }
                                shopName={preferredStore?.storeName}
                            />
                        )}
                        {!product?.boxArticle && !preferredStore ? (
                            <p
                                data-test={`deliveryItem-${option.deliveryType}-description`}
                                className="delivery-item__description"
                            >
                                {t("general.ecommerce.clickAndCollect.noStore")}
                            </p>
                        ) : (
                            !product?.boxArticle &&
                            !!preferredStore?.productAvailability && (
                                <ItemStock
                                    data-test={`deliveryItem-${option.deliveryType}-itemStock`}
                                    productAvailability={productAvailability!}
                                    shopName={preferredStore?.storeName}
                                />
                            )
                        )}
                        <Button
                            test={`deliveryItem-${option.deliveryType}-button-showAvailability`}
                            color={"link"}
                            className={"p-0"}
                            type="button"
                            onClick={() => {
                                handleClick(option.deliveryType);
                                setIsStorePickerOpen(true);
                            }}
                        >
                            {t("general.ecommerce.link.showAvailability")}
                            <i className="las la-angle-right mr-1" />
                        </Button>
                    </div>
                ) : (
                    <p
                        data-test={`deliveryItem-${option.deliveryType}-deliveryPromise`}
                        className="delivery-item__description"
                    >
                        {t(
                            determineDeliveryTimeTranslationKey(
                                option.deliveryType,
                                preferredStore?.productAvailability?.stockLevel,
                                option.realTimeStockAvailable,
                            ) || "",
                            option.deliveryTime,
                            deliveryTimeUnitTk,
                            option.realTimeStock,
                        )}
                    </p>
                )}
                {option.deliveryType === DeliveryTypes.HomeDelivery &&
                    minimumOrderQuantity > 1 && (
                        <div>
                            {roundingValue > 1 ? (
                                <p
                                    data-test={`deliveryItem-${option.deliveryType}-roundingValue`}
                                    className="delivery-item__description"
                                >
                                    {t(
                                        "general.ecommerce.roundingValue",
                                        roundingValue,
                                    )}
                                </p>
                            ) : (
                                <p
                                    data-test={`deliveryItem-${option.deliveryType}-minimumOrderQuantity`}
                                    className="delivery-item__description"
                                >
                                    {t(
                                        "general.ecommerce.minimumOrderQuantity",
                                        minimumOrderQuantity,
                                    )}
                                </p>
                            )}
                        </div>
                    )}
                {option.deliveryType === DeliveryTypes.HomeDelivery &&
                    xfPath && (
                        <a
                            data-test={`deliveryItem-${option.deliveryType}-supplierInformation`}
                            className="delivery-item__description"
                            href="#supplier-info"
                        >
                            {t("ecommerce.label.supplierInformation")}
                        </a>
                    )}
                {product?.references.montages?.map(serviceProduct => (
                    <AdditionalService
                        key={serviceProduct.sku}
                        serviceProduct={serviceProduct}
                        onServiceProductClick={onServiceProductClick}
                        activeServiceProducts={
                            checked ? activeServiceProducts : []
                        }
                        tooltip={tooltips.serviceProduct}
                        documents={serviceProduct.documents}
                    />
                ))}
            </div>
            <StorePickerModal
                isOpen={isStorePickerOpen}
                type={StorePickerType.SELECT}
                storeCode={preferredStore?.code ?? null}
                displayStockLevel={!product?.boxArticle}
                infoLink={infoLink}
                fixedFeatures={
                    productIsClickAndCollect
                        ? [StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE]
                        : []
                }
                onSubmit={(_, close) => close?.()}
                onClose={handleOnClose}
            />
        </>
    );
};

export default DeliveryItem;
